import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  ChartBarIcon,
  InformationCircleIcon,
  NewspaperIcon,
} from "@heroicons/react/24/outline";
import { headlinesApi } from "../api/cmsApi";
import toast from "react-hot-toast";

// Available template variables for headlines
const templateVariables = [
  { key: "{{averagePayout}}", description: "Ø Auszahlung (z.B. 347)" },
  { key: "{{claimsProcessed}}", description: "Anzahl Kunden (z.B. 4.823)" },
  { key: "{{successRate}}", description: "Erfolgsquote (z.B. 94)" },
  { key: "{{totalPaidOut}}", description: "Gesamt ausgezahlt (z.B. 1.247.832)" },
  { key: "{{processingTime}}", description: "Bearbeitungszeit in Tagen (z.B. 3)" },
];

interface Headline {
  _id: string;
  variant: string;
  section: string;
  headline: { de: string; en: string };
  subheadline: { de: string; en: string };
  ctaText: { de: string; en: string };
  badge?: { de: string; en: string };
  footer?: { de: string; en: string };
  isActive: boolean;
  weight: number;
  impressions: number;
  clicks: number;
  conversions: number;
}

const variants = [
  { id: "default", label: "Standard (Google/Direct)" },
  { id: "facebook", label: "Facebook" },
  { id: "instagram", label: "Instagram" },
  { id: "x", label: "X (Twitter)" },
  { id: "linkedin", label: "LinkedIn" },
  { id: "reddit", label: "Reddit" },
  { id: "tiktok", label: "TikTok" },
  { id: "about", label: "About / Über uns" },
];

const sections = [
  { id: "hero", label: "Hero" },
  { id: "trustIndicators", label: "Trust Indicators" },
  { id: "calculator", label: "Calculator" },
  { id: "howItWorks", label: "How It Works" },
  { id: "socialPost", label: "Social Post Mockup" },
  { id: "faqVideos", label: "FAQ Videos" },
  { id: "comparison", label: "Comparison / Testimonials" },
  { id: "features", label: "Features / Benefits" },
  { id: "whyClaroclaim", label: "Why Claroclaim" },
  { id: "faq", label: "FAQ" },
  { id: "cta", label: "CTA" },
  { id: "mission", label: "Mission (About)" },
  { id: "values", label: "Values (About)" },
  { id: "team", label: "Team (About)" },
  { id: "contact", label: "Contact (About)" },
];

export default function Headlines() {
  const queryClient = useQueryClient();
  const [selectedVariant, setSelectedVariant] = useState("default");
  const [selectedSection, setSelectedSection] = useState("hero");
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    section: "hero",
    headline: { de: "", en: "" },
    subheadline: { de: "", en: "" },
    ctaText: { de: "", en: "" },
    badge: { de: "", en: "" },
    footer: { de: "", en: "" },
    weight: 100,
  });

  const { data: headlines = [], isLoading } = useQuery({
    queryKey: ["headlines", selectedVariant, selectedSection],
    queryFn: () => headlinesApi.list(selectedVariant, selectedSection).then((res) => res.data.data || []),
  });

  const createMutation = useMutation({
    mutationFn: (data: typeof formData & { variant: string }) =>
      headlinesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["headlines"] });
      setIsCreating(false);
      resetForm();
      toast.success("Headline erstellt");
    },
    onError: () => toast.error("Fehler beim Erstellen"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Headline> }) =>
      headlinesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["headlines"] });
      setEditingId(null);
      resetForm();
      toast.success("Headline aktualisiert");
    },
    onError: () => toast.error("Fehler beim Aktualisieren"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => headlinesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["headlines"] });
      toast.success("Headline gelöscht");
    },
    onError: () => toast.error("Fehler beim Löschen"),
  });

  const resetForm = () => {
    setFormData({
      section: selectedSection,
      headline: { de: "", en: "" },
      subheadline: { de: "", en: "" },
      ctaText: { de: "", en: "" },
      badge: { de: "", en: "" },
      footer: { de: "", en: "" },
      weight: 100
    });
  };

  const handleCreate = () => {
    createMutation.mutate({ ...formData, variant: selectedVariant, section: formData.section });
  };

  const handleUpdate = (id: string) => {
    updateMutation.mutate({ id, data: formData });
  };

  const handleToggleActive = (headline: Headline) => {
    updateMutation.mutate({
      id: headline._id,
      data: { isActive: !headline.isActive },
    });
  };

  const startEditing = (headline: Headline) => {
    setEditingId(headline._id);
    setFormData({
      section: headline.section || "hero",
      headline: headline.headline || { de: "", en: "" },
      subheadline: headline.subheadline || { de: "", en: "" },
      ctaText: headline.ctaText || { de: "", en: "" },
      badge: headline.badge || { de: "", en: "" },
      footer: headline.footer || { de: "", en: "" },
      weight: headline.weight,
    });
  };

  const calcCTR = (impressions: number, clicks: number) => {
    if (impressions === 0) return "0%";
    return `${((clicks / impressions) * 100).toFixed(1)}%`;
  };

  const calcConvRate = (clicks: number, conversions: number) => {
    if (clicks === 0) return "0%";
    return `${((conversions / clicks) * 100).toFixed(1)}%`;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div className="flex items-center space-x-3">
          <NewspaperIcon className="h-8 w-8 text-gray-700 dark:text-gray-300" />
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Headlines</h1>
            <p className="text-gray-600 dark:text-gray-400">
              Verwalten Sie die Headlines für A/B-Tests auf verschiedenen Kanälen
            </p>
          </div>
        </div>
        <button
          onClick={() => {
            setIsCreating(true);
            resetForm();
          }}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Neue Headline
        </button>
      </div>

      {/* Template Variables Info */}
      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <div className="flex items-start gap-3">
          <InformationCircleIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-medium text-blue-900 dark:text-blue-200 mb-2">Dynamische Variablen</h4>
            <p className="text-sm text-blue-800 dark:text-blue-300 mb-2">
              Du kannst folgende Variablen in Headlines verwenden. Sie werden automatisch durch aktuelle Werte ersetzt:
            </p>
            <div className="flex flex-wrap gap-2">
              {templateVariables.map((v) => (
                <span
                  key={v.key}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 text-xs rounded font-mono"
                  title={v.description}
                >
                  {v.key}
                  <span className="text-blue-600 dark:text-blue-400 font-sans">({v.description})</span>
                </span>
              ))}
            </div>
            <p className="text-xs text-blue-700 dark:text-blue-400 mt-2">
              Beispiel: "Durchschnittlich {"{{averagePayout}}"}€ Auszahlung" wird zu "Durchschnittlich 347€ Auszahlung"
            </p>
          </div>
        </div>
      </div>

      {/* Variant Tabs */}
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wide">
            Kanal / Variante
          </label>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {variants.map((variant) => (
              <button
                key={variant.id}
                onClick={() => setSelectedVariant(variant.id)}
                className={`
                  px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors
                  ${selectedVariant === variant.id
                    ? "bg-indigo-600 text-white dark:bg-indigo-500"
                    : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700"
                  }
                `}
              >
                {variant.label}
              </button>
            ))}
          </div>
        </div>

        {/* Section Tabs */}
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wide">
            Sektion
          </label>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setSelectedSection(section.id)}
                className={`
                  px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors
                  ${selectedSection === section.id
                    ? "bg-emerald-600 text-white dark:bg-emerald-500"
                    : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700"
                  }
                `}
              >
                {section.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Create Form */}
      {isCreating && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg mb-6">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Neue Headline erstellen</h3>
            <div className="space-y-4">
              {/* Headline DE/EN */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Headline</label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">Deutsch</span>
                    <textarea
                      value={formData.headline.de}
                      onChange={(e) => setFormData({ ...formData, headline: { ...formData.headline, de: e.target.value } })}
                      className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      rows={2}
                      placeholder="Deutsche Überschrift"
                    />
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">English</span>
                    <textarea
                      value={formData.headline.en}
                      onChange={(e) => setFormData({ ...formData, headline: { ...formData.headline, en: e.target.value } })}
                      className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      rows={2}
                      placeholder="English headline"
                    />
                  </div>
                </div>
              </div>

              {/* Subheadline DE/EN */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Subheadline</label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">Deutsch</span>
                    <textarea
                      value={formData.subheadline.de}
                      onChange={(e) => setFormData({ ...formData, subheadline: { ...formData.subheadline, de: e.target.value } })}
                      className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      rows={2}
                      placeholder="Deutsche Unterüberschrift"
                    />
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">English</span>
                    <textarea
                      value={formData.subheadline.en}
                      onChange={(e) => setFormData({ ...formData, subheadline: { ...formData.subheadline, en: e.target.value } })}
                      className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      rows={2}
                      placeholder="English subheadline"
                    />
                  </div>
                </div>
              </div>

              {/* Badge and Footer DE/EN */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Badge (optional)</label>
                  <div className="space-y-2">
                    <div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">Deutsch</span>
                      <input
                        type="text"
                        value={formData.badge.de}
                        onChange={(e) => setFormData({ ...formData, badge: { ...formData.badge, de: e.target.value } })}
                        className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        placeholder="z.B. So funktioniert's"
                      />
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">English</span>
                      <input
                        type="text"
                        value={formData.badge.en}
                        onChange={(e) => setFormData({ ...formData, badge: { ...formData.badge, en: e.target.value } })}
                        className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        placeholder="e.g. How it works"
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Footer (optional)</label>
                  <div className="space-y-2">
                    <div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">Deutsch</span>
                      <input
                        type="text"
                        value={formData.footer.de}
                        onChange={(e) => setFormData({ ...formData, footer: { ...formData.footer, de: e.target.value } })}
                        className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        placeholder="z.B. Kein Risiko - Keine versteckten Kosten"
                      />
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">English</span>
                      <input
                        type="text"
                        value={formData.footer.en}
                        onChange={(e) => setFormData({ ...formData, footer: { ...formData.footer, en: e.target.value } })}
                        className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        placeholder="e.g. No risk - No hidden costs"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* CTA and Weight */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">CTA-Text</label>
                  <div className="space-y-2">
                    <div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">Deutsch</span>
                      <input
                        type="text"
                        value={formData.ctaText.de}
                        onChange={(e) => setFormData({ ...formData, ctaText: { ...formData.ctaText, de: e.target.value } })}
                        className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        placeholder="z.B. Jetzt prüfen"
                      />
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">English</span>
                      <input
                        type="text"
                        value={formData.ctaText.en}
                        onChange={(e) => setFormData({ ...formData, ctaText: { ...formData.ctaText, en: e.target.value } })}
                        className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        placeholder="e.g. Check now"
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Gewichtung</label>
                  <input
                    type="number"
                    value={formData.weight}
                    onChange={(e) => setFormData({ ...formData, weight: parseInt(e.target.value) || 100 })}
                    className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    min="1"
                    max="1000"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Höher = häufiger angezeigt
                  </p>
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setIsCreating(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleCreate}
                  disabled={createMutation.isPending}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 disabled:opacity-50"
                >
                  {createMutation.isPending ? "Erstellen..." : "Erstellen"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Headlines List */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">Laden...</div>
        ) : headlines.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-12 text-center">
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              Keine Headlines für diese Variante vorhanden.
            </p>
            <button
              onClick={() => setIsCreating(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
            >
              Erste Headline erstellen
            </button>
          </div>
        ) : (
          headlines.map((headline: Headline) => (
            <div key={headline._id} className="bg-white dark:bg-gray-800 shadow rounded-lg">
              {editingId === headline._id ? (
                <div className="p-6">
                  <div className="space-y-4">
                    {/* Headline DE/EN */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Headline</label>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">Deutsch</span>
                          <textarea
                            value={formData.headline.de}
                            onChange={(e) => setFormData({ ...formData, headline: { ...formData.headline, de: e.target.value } })}
                            className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            rows={2}
                          />
                        </div>
                        <div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">English</span>
                          <textarea
                            value={formData.headline.en}
                            onChange={(e) => setFormData({ ...formData, headline: { ...formData.headline, en: e.target.value } })}
                            className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            rows={2}
                          />
                        </div>
                      </div>
                    </div>
                    {/* Subheadline DE/EN */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Subheadline</label>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">Deutsch</span>
                          <textarea
                            value={formData.subheadline.de}
                            onChange={(e) => setFormData({ ...formData, subheadline: { ...formData.subheadline, de: e.target.value } })}
                            className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            rows={2}
                          />
                        </div>
                        <div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">English</span>
                          <textarea
                            value={formData.subheadline.en}
                            onChange={(e) => setFormData({ ...formData, subheadline: { ...formData.subheadline, en: e.target.value } })}
                            className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            rows={2}
                          />
                        </div>
                      </div>
                    </div>
                    {/* Badge and Footer DE/EN */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Badge (optional)</label>
                        <div className="space-y-2">
                          <div>
                            <span className="text-xs text-gray-500 dark:text-gray-400">Deutsch</span>
                            <input
                              type="text"
                              value={formData.badge.de}
                              onChange={(e) => setFormData({ ...formData, badge: { ...formData.badge, de: e.target.value } })}
                              className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            />
                          </div>
                          <div>
                            <span className="text-xs text-gray-500 dark:text-gray-400">English</span>
                            <input
                              type="text"
                              value={formData.badge.en}
                              onChange={(e) => setFormData({ ...formData, badge: { ...formData.badge, en: e.target.value } })}
                              className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            />
                          </div>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Footer (optional)</label>
                        <div className="space-y-2">
                          <div>
                            <span className="text-xs text-gray-500 dark:text-gray-400">Deutsch</span>
                            <input
                              type="text"
                              value={formData.footer.de}
                              onChange={(e) => setFormData({ ...formData, footer: { ...formData.footer, de: e.target.value } })}
                              className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            />
                          </div>
                          <div>
                            <span className="text-xs text-gray-500 dark:text-gray-400">English</span>
                            <input
                              type="text"
                              value={formData.footer.en}
                              onChange={(e) => setFormData({ ...formData, footer: { ...formData.footer, en: e.target.value } })}
                              className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* CTA and Weight */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">CTA-Text</label>
                        <div className="space-y-2">
                          <div>
                            <span className="text-xs text-gray-500 dark:text-gray-400">Deutsch</span>
                            <input
                              type="text"
                              value={formData.ctaText.de}
                              onChange={(e) => setFormData({ ...formData, ctaText: { ...formData.ctaText, de: e.target.value } })}
                              className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            />
                          </div>
                          <div>
                            <span className="text-xs text-gray-500 dark:text-gray-400">English</span>
                            <input
                              type="text"
                              value={formData.ctaText.en}
                              onChange={(e) => setFormData({ ...formData, ctaText: { ...formData.ctaText, en: e.target.value } })}
                              className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            />
                          </div>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Gewichtung</label>
                        <input
                          type="number"
                          value={formData.weight}
                          onChange={(e) => setFormData({ ...formData, weight: parseInt(e.target.value) || 100 })}
                          className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => setEditingId(null)}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                      >
                        <XMarkIcon className="h-4 w-4 mr-1" />
                        Abbrechen
                      </button>
                      <button
                        onClick={() => handleUpdate(headline._id)}
                        disabled={updateMutation.isPending}
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 disabled:opacity-50"
                      >
                        <CheckIcon className="h-4 w-4 mr-1" />
                        Speichern
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <div className="grid grid-cols-2 gap-4 mb-3">
                        <div>
                          <span className="text-xs text-gray-400 uppercase">DE</span>
                          <h3 className="font-semibold text-gray-900 dark:text-white whitespace-pre-line">
                            {headline.headline?.de || (typeof headline.headline === 'string' ? headline.headline : '-')}
                          </h3>
                          <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
                            {headline.subheadline?.de || (typeof headline.subheadline === 'string' ? headline.subheadline : '-')}
                          </p>
                        </div>
                        <div>
                          <span className="text-xs text-gray-400 uppercase">EN</span>
                          <h3 className="font-semibold text-gray-900 dark:text-white whitespace-pre-line">
                            {headline.headline?.en || '-'}
                          </h3>
                          <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">{headline.subheadline?.en || '-'}</p>
                        </div>
                      </div>
                      <p className="text-indigo-600 dark:text-indigo-400 text-sm">
                        CTA: {headline.ctaText?.de || (typeof headline.ctaText === 'string' ? headline.ctaText : '-')} / {headline.ctaText?.en || '-'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleToggleActive(headline)}
                        className={`
                          px-3 py-1 rounded-full text-xs font-medium
                          ${headline.isActive
                            ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                            : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
                          }
                        `}
                      >
                        {headline.isActive ? "Aktiv" : "Inaktiv"}
                      </button>
                      <button
                        onClick={() => startEditing(headline)}
                        className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          if (confirm("Headline wirklich löschen?")) {
                            deleteMutation.mutate(headline._id);
                          }
                        }}
                        className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="flex items-center gap-6 pt-4 border-t border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                      <ChartBarIcon className="h-4 w-4" />
                      <span>Gewichtung: {headline.weight}</span>
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      Impressions: {(headline.impressions || 0).toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      CTR: {calcCTR(headline.impressions || 0, headline.clicks || 0)}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      Conv: {calcConvRate(headline.clicks || 0, headline.conversions || 0)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
